import { checkedTrackedItems } from "../../lib/tracked-items";
import { useEffect, useRef, useState } from "react";
import type { Product, ReceiptDraft, Tracking } from "../../types/receipt";
import { api, ApiError } from "../../lib/api";

/**
 * Recording what each item on a saved receipt cost. Every item is written to this device before it
 * is sent and marked saved only once the server verifies it, so an interrupted run resumes where it
 * stopped — an item whose outcome is unknown shows "Check save" rather than being sent twice.
 */
export function useReceiptTracking({ draft, initial, initialSelectedIds, onDone }: {
  draft: ReceiptDraft;
  initial: Tracking[];
  initialSelectedIds: string[];
  onDone?: () => void;
}) {
  const key = `barnyard-price-pending-${draft.id}`;
  const [products, setProducts] = useState<Product[]>([]),
    [items, setItems] = useState(draft.items),
    [selected, setSelected] = useState<string[]>([]),
    [statuses, setStatuses] = useState<Record<string, "pending" | "saved">>({}),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [message, setMessage] = useState("");
  const mounted = useRef(true),
    saving = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    let active = true;
    const restored: Tracking[] = (() => {
      try {
        return JSON.parse(localStorage.getItem(key) || "[]");
      } catch {
        return [];
      }
    })();
    const overrides = [...restored, ...initial];
    setItems(
      draft.items.map(
        (i) =>
          [...overrides].reverse().find((o) => o.itemId === i.id)?.item || i,
      ),
    );
    setStatuses(
      Object.fromEntries(
        overrides.map((o) => [o.itemId, o.saved ? "saved" : "pending"]),
      ),
    );
    setSelected([
      ...new Set([
        ...overrides.filter((o) => !o.saved).map((o) => o.itemId),
        ...initialSelectedIds,
      ]),
    ]);
    api<{ products: Product[]; aliases: Record<string, string> }>("price-catalog")
      .then((c) => {
        if (!active) return;
        setProducts(c.products);
        setItems((current) =>
          current.map((i) => {
            if (overrides.some((o) => o.itemId === i.id)) return i;
            const norm = (s: string) =>
              s.normalize("NFKC").trim().toLowerCase().replace(/\s+/g, " ");
            const id =
              c.aliases[
                JSON.stringify([norm(draft.expense.title), norm(i.original)])
              ];
            const product = c.products.find((p: Product) => p.id === id);
            return product
              ? {
                  ...i,
                  name: product.name,
                  brand: product.brand,
                  group: product.group,
                }
              : i;
          }),
        );
      })
      .catch(() => {
        if (active)
          setError(
            "Product suggestions are unavailable. You can still enter custom names.",
          );
      });
    return () => {
      active = false;
    };
  // Suggestions load once per draft and selection; later edits to items must not refetch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.id, initialSelectedIds.join("|")]);
  async function save() {
    if (saving.current) return;
    saving.current = true;
    setBusy(true);
    setError("");
    setMessage("");
    let journal: Tracking[] = items
      .filter((i) => statuses[i.id])
      .map((item) => ({
        itemId: item.id,
        item,
        saved: statuses[item.id] === "saved",
      }));
    try {
      const chosen = items.filter(
        (i) => selected.includes(i.id) && statuses[i.id] !== "saved",
      );
      checkedTrackedItems(chosen.map(item => ({ ...item, notes: "" })));
      for (const item of chosen) {
        if (!mounted.current) break;
        const entry = { itemId: item.id, item, saved: false };
        journal = [...journal.filter((j) => j.itemId !== item.id), entry];
        localStorage.setItem(key, JSON.stringify(journal));
        setStatuses((s) => ({ ...s, [item.id]: "pending" }));
        let result;
        try {
          result = await api<{ verified: boolean }>("prices", { receiptId: draft.id, item });
        } catch (e) {
          if (e instanceof ApiError && !e.uncertain) {
            journal = journal.filter((j) => j.itemId !== item.id);
            localStorage.setItem(key, JSON.stringify(journal));
            if (mounted.current)
              setStatuses((s) => {
                const n = { ...s };
                delete n[item.id];
                return n;
              });
          }
          throw e;
        }
        if (!result.verified)
          throw new Error("Price verification is pending. Try again.");
        entry.saved = true;
        localStorage.setItem(key, JSON.stringify(journal));
        if (mounted.current) setStatuses((s) => ({ ...s, [item.id]: "saved" }));
      }
      if (mounted.current) {
        setMessage("Selected prices saved and verified.");
        onDone?.();
      }
    } catch (e) {
      if (mounted.current)
        setError(
          e instanceof Error ? e.message : "Could not save prices. Try again.",
        );
    } finally {
      saving.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  const remaining = selected.filter((id) => statuses[id] !== "saved").length;
  return { products, items, setItems, selected, setSelected, statuses, busy, error, message, remaining, save };
}
